import React, { useState, useEffect, useRef } from 'react';

// Sistema de cola para limitar solicitudes concurrentes (global)
const driveImageRequestQueue = {
    queue: [],
    activeRequests: 0,
    maxConcurrent: 5,
    cache: {}, // Cache global compartido entre componentes

    enqueue(task) {
        this.queue.push(task);
        this.processQueue();
    },

    processQueue() {
        if (this.queue.length === 0 || this.activeRequests >= this.maxConcurrent) return;

        this.activeRequests++;
        const nextTask = this.queue.shift();

        nextTask().finally(() => {
            this.activeRequests--;
            this.processQueue();
        });
    }
};

const DriveImg = ({ driveId, alt = "image", className, style, id, fallbackImage = '/img/png/thumbnail.png', onLoad, ...props }) => {
    const [imgSrc, setImgSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const imgRef = useRef(null);

    // Lazy loading - solo carga cuando es visible
    useEffect(() => {
        if (!imgRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsVisible(true);
                observer.disconnect();
            }
        }, { threshold: 0.1 });

        observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, []);

    // Cuando se hace visible, carga la imagen
    useEffect(() => {
        if (!isVisible || !isLoading || !driveId) return;

        // Si ya está en caché, usamos esa URL
        if (driveImageRequestQueue.cache[driveId]) {
            setImgSrc(driveImageRequestQueue.cache[driveId]);
            setIsLoading(false);
            return;
        }

        const loadImage = () => {
            return new Promise((resolve) => {
                // URLs a intentar
                const urlFormats = [
                    `https://lh3.googleusercontent.com/d/${driveId}`,
                    `https://drive.google.com/thumbnail?id=${driveId}`,
                ];

                // Función recursiva para probar URLs
                const tryNextUrl = (index = 0) => {
                    if (index >= urlFormats.length) {
                        // Si todas fallan, usar fallback
                        driveImageRequestQueue.cache[driveId] = fallbackImage;
                        setImgSrc(fallbackImage);
                        setIsLoading(false);
                        resolve();
                        return;
                    }

                    const img = new Image();
                    img.onload = () => {
                        // Éxito - guardamos en caché y usamos
                        driveImageRequestQueue.cache[driveId] = urlFormats[index];
                        setImgSrc(urlFormats[index]);
                        setIsLoading(false);
                        resolve();
                    };

                    img.onerror = () => {
                        // Error - intentar siguiente URL
                        tryNextUrl(index + 1);
                    };

                    img.src = urlFormats[index];
                };

                tryNextUrl();
            });
        };

        // Agregar a la cola
        driveImageRequestQueue.enqueue(loadImage);
    }, [driveId, isVisible, isLoading, fallbackImage]);

    return (
        <div ref={imgRef} style={{ display: 'inline-block', ...style }}>
            {imgSrc && (
                <img
                    src={imgSrc}
                    alt={alt}
                    className={className}
                    id={id}
                    onLoad={onLoad}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = fallbackImage;
                    }}
                    {...props}
                />
            )}
        </div>
    );
};

export default DriveImg;